<template>
  <div
    class="certification-tile"
    :class="{ 'certification-tile__acctionable': !pendingReview }"
  >
    <div class="certification-tile__user-status">
      <CertStatus
        :status="certificationUserStatusText || ''"
        :showStatus="showStatus"
      />
    </div>
    <PillBadge
      v-if="isInBeta"
      class="certification-tile__beta-pill"
      translucid
      sober
      soberBlackFlat
    >
      BETA
    </PillBadge>
    <div class="certification-tile__wrapper">
      <div class="certification-tile__badge">
        <img
          class="certification-tile__badge-logo"
          :src="certification.logo_image_url"
        />
      </div>
      <h3 class="certification-tile__title">
        {{ certification.name }}
      </h3>
      <CertificationDetails
        v-if="showDetails"
        :difficulty="certification.difficulty || ''"
        :timeLimit="timeCap"
        :questions="certification.amount_questions || 0"
        :objectives="labObjectives"
      />
      <div v-if="!startAvailable" class="certification-tile__not-available">
        <AlertIcon />
        <div>
          The exam will be available on
          <span
            v-tooltip="{
              content: `${availableDateTime} ${localTimeZone}`,
              placement: 'top-center',
            }"
          >
            {{ availableOnDate }}</span
          >.
        </div>
      </div>
      <div v-if="showDetails" class="certification-tile__bottom">
        <router-link
          v-if="attemptPassed"
          :to="{
            name: 'user-dashboard',
            params: { mode: 'certifications' },
          }"
          class="btn btn--secondary certification-tile__button certification-tile__button-bottom"
          >{{ buttonText }}</router-link
        >
        <a
          v-if="(hasNoVouchers && !isInBeta) || (noUserStatus && !isInBeta)"
          :href="`//${certificationRedirectURL(certification.id, isInBeta)}`"
          target="_blank"
          rel="noopener nofollow"
          class="btn btn--secondary certification-tile__button"
        >
          Shop vouchers
        </a>

        <a
          v-if="(hasNoVouchers && isInBeta) || (noUserStatus && isInBeta)"
          :href="
            `${currentVersion.beta_link ||
              `//${certificationRedirectURL(certification.id, isInBeta)}`}`
          "
          target="_blank"
          rel="noopener nofollow"
          class="btn btn--secondary certification-tile__button"
        >
          Learn more
        </a>
        <button
          v-if="showButton"
          :disabled="!startAvailable"
          class="btn btn--secondary certification-tile__button certification-tile__button-bottom"
          @click="setCertificationAndGo()"
        >
          {{ buttonText }}
        </button>
        <a
          v-if="hasLearningPath"
          class="btn btn--tertiary certification-tile__training-link"
          @click.prevent="handleGetTrainingClick"
        >
          Get training
        </a>
      </div>
      <span v-if="pendingReview" class="certification-tile__review-status">
        We're reviewing your exam. You'll get a notification when the results
        are in.</span
      >
    </div>
  </div>
</template>

<script>
import CertStatus from '@/components/properties/CertStatus.vue'
import CertificationDetails from '@/components/certifications/CertificationDetails.vue'
import CONSTANTS from '@/constants'
import {
  hasPassedAttempt,
  noCurrentVoucher,
  certInBeta,
  canNotTakeExam,
  activeVouchers,
  attemptInProgress,
  certLatestVersion,
  hasUserStatus,
  lastActiveVoucherFromCert,
  isCertAvailable,
  certAvailableDate,
  certAvailableDateTime,
} from '@/helpers'
import { localTimezone, certificationCommons } from '@/helpers/mixins'
import AlertIcon from '@/assets/icons/alert-circle-10.svg'
import PillBadge from '@/components/shared/PillBadge.vue'

export default {
  name: 'CertificationTile',
  components: {
    CertStatus,
    CertificationDetails,
    AlertIcon: { ...AlertIcon, name: 'AlertIcon' },
    PillBadge,
  },
  mixins: [localTimezone, certificationCommons],
  props: {
    certification: {
      type: Object,
      required: true,
    },
    lab: {
      type: Object,
      required: false,
    },
    showDetails: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  computed: {
    noUserStatus() {
      return !hasUserStatus(this.certification)
    },
    isEJPT() {
      return this.certification.id === CONSTANTS.CERTIFICATIONS.IDS.eJPT
    },
    attemptPassed() {
      const attempt = this.lastAttempt
      return hasPassedAttempt(attempt)
    },
    hasNoVouchers() {
      return noCurrentVoucher(this.certification)
    },
    isInBeta() {
      return certInBeta(this.certification)
    },
    isInBetaRedirect() {
      return (
        (this.hasNoVouchers && this.isInBeta) ||
        (this.noUserStatus && this.isInBeta)
      )
    },
    statusVersionBeta() {
      const {
        status,
        latest_version: latestVersion, // check field name
      } = this.certification

      if (this.isInBeta) return `v${latestVersion} BETA`

      if (status === CONSTANTS.CERTIFICATIONS.STATUS.COMING_SOON) {
        return CONSTANTS.CERTIFICATIONS.STATUS.COMING_SOON.replace(/_/g, ' ')
      }

      return `v${latestVersion}`
    },
    learningPath() {
      return {
        learning_areas: [this.certification.learning_area],
        ...this.certification.learning_paths[0],
      }
    },
    showStatus() {
      return (
        this.userStatusCert ===
          CONSTANTS.CERTIFICATIONS.USER_STATUS.ATTEMPT_IN_REVIEW ||
        this.userStatusCert === CONSTANTS.CERTIFICATIONS.USER_STATUS.AWARDED
      )
    },
    showButton() {
      return (
        !canNotTakeExam(this.certification) &&
        this.userStatusCert !==
          CONSTANTS.CERTIFICATIONS.USER_STATUS.ATTEMPT_IN_REVIEW &&
        this.userStatusCert !== CONSTANTS.CERTIFICATIONS.USER_STATUS.AWARDED &&
        this.userStatusCert !==
          CONSTANTS.CERTIFICATIONS.USER_STATUS.NOT_AVAILABLE
      )
    },
    certificationUserStatusText() {
      return this.userStatusCert.replace(/_/g, ' ')
    },
    pendingReview() {
      return (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.ATTEMPT_IN_REVIEW
      )
    },
    timeCap() {
      const certVersion = certLatestVersion(this.certification)
      const durationInSecs = certVersion
        ? certVersion.time_cap_in_secs
        : this.certification.time_cap_in_secs
      return this.$duration.durationFromSecs(durationInSecs, 'HOURSMIN')
    },
    labObjectives() {
      return this.lab ? this.lab.lab_objectives?.length : 0
    },
    hasLearningPath() {
      return (
        this.certification.learning_paths?.length &&
        this.certification.learning_paths[0]
      )
    },
    startAvailable() {
      return isCertAvailable(this.certification)
    },
    availableOnDate() {
      return certAvailableDate(this.certification)
    },
    lastActiveVoucher() {
      return lastActiveVoucherFromCert(this.certification)
    },
    availableDateTime() {
      return certAvailableDateTime(this.certification)
    },
  },
  methods: {
    setCertificationAndGo() {
      this.$store.dispatch(
        'certifications/updateCurrentCertification',
        this.certification
      )
      const attempt = this.lastAttempt
      this.$router.push({
        name: 'certification-exam',
        params: {
          voucherId: activeVouchers(this.certification)[0].id,
          attemptId: attemptInProgress(attempt)
            ? attemptInProgress(attempt).id
            : null,
        },
      })
    },
    handleGetTrainingClick() {
      if (this.hasLearningPath) {
        this.getTrainingClick()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.certification-tile {
  $this: &;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(
    104.99deg,
    rgba(62, 63, 73, 0.7) -13.91%,
    rgba(62, 63, 73, 0) 100.48%
  );
  border-radius: 10px;
  width: 100%;
  min-height: 560px;
  position: relative;
  @include mobile {
    width: calc(50% - 10px);
  }
  @include tablet {
    width: calc(33% - 10px);
  }
  &__acctionable {
    cursor: pointer;
  }
  &__wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0px 30px 30px 30px;
    justify-content: space-between;
  }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: flex-end;
    height: 32px;
  }
  &__status {
    position: relative;
    box-sizing: border-box;
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 20px;
    color: var(--white);
    text-transform: uppercase;
    padding: 9px 14px;
    font-size: 12px;
    line-height: 14px;
    &:before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: -1;
      margin: -2px; /* !importanté */
      border-radius: inherit; /* !importanté */
      background: linear-gradient(
        111.02deg,
        #3e3f49b2 3.86%,
        #3e3f4900 107.96%
      );
    }
  }
  &__user-status {
    position: absolute;
    top: 0;
  }
  &__badge {
    display: flex;
    justify-content: center;
    margin-top: 10px;
    &-logo {
      height: 200px;
    }
  }
  &__title {
    font-weight: bold;
    font-size: 20px;
    line-height: 26px;
    display: flex;
    align-items: flex-end;
    color: var(--white);
    margin: 15px 0;
  }
  &__description {
    font-size: 16px;
    line-height: 24px;
    color: var(--light-blue-gray);
  }
  &__bottom {
    display: flex;
    align-items: center;
    flex-direction: column-reverse;
    padding-top: 20px;

    @include tablet {
      flex-direction: row-reverse;
      justify-content: space-between;
    }
  }
  &__training-link {
    color: var(--white);
    font-weight: 500;
    font-size: 16px;
    line-height: 23px;
  }
  &__button {
    align-self: center;
    width: 100%;
    @include tablet {
      align-self: flex-end;
      width: 50%;
    }

    &-bottom {
      min-width: 140px;
    }
  }
  &__review-status {
    color: var(--mid-gray);
  }
  &__review-status:before {
    content: 'Pending Review:';
    color: #ffffff;
  }
  &__not-available {
    display: flex;
    color: var(--light-blue-gray);
    margin-top: 22px;
    align-items: center;
    svg {
      margin-right: 15px;
    }
  }
  &__start-disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  &__timezone-icon {
    font-size: 16px !important;
    margin-left: 5px;
    color: var(--gray);
  }
  &__beta-pill {
    position: absolute;
    top: 20px;
    right: 20px;
  }
}
</style>
